import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/Header';
import LandingPageUpdated from './components/LandingPageUpdated';
import ConfiguratorStep from './components/ConfiguratorStep';
import PoleSelection from './components/PoleSelection';
import AccessorySelection from './components/AccessorySelection';
import Summary from './components/Summary';
import LoadingSpinner from './components/LoadingSpinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [currentStep, setCurrentStep] = useState(0); // Start mit Landingpage
  const [configuration, setConfiguration] = useState({
    poles: [],
    accessories: [],
    accessory_quantities: {},
    transport_option: {},
    customer_info: {}
  });
  const [poles, setPoles] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Scroll to Top bei Schritt-Wechsel
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Schritt-Konfiguration
  const steps = [
    { id: 0, title: 'Willkommen', description: 'Entdecken Sie unsere Xortec Kameramasten' },
    { id: 1, title: 'Masttyp wählen', description: 'Wählen Sie Ihren gewünschten Kameramast' },
    { id: 2, title: 'Zubehör auswählen', description: 'Ergänzen Sie Ihre Konfiguration mit passendem Zubehör' },
    { id: 3, title: 'Zusammenfassung', description: 'Überprüfen Sie Ihre Konfiguration und exportieren Sie als Excel' }
  ];

  // Daten beim Start laden
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [polesResponse, accessoriesResponse] = await Promise.all([
        fetch(`${BACKEND_URL}/api/poles`),
        fetch(`${BACKEND_URL}/api/accessories`)
      ]);

      const polesData = await polesResponse.json();
      const accessoriesData = await accessoriesResponse.json();

      setPoles(polesData.poles || []);
      setAccessories(accessoriesData.accessories || []);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigation Funktionen
  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleStartConfiguration = () => {
    setCurrentStep(1); // Springe direkt zum ersten Konfigurationsschritt
    if (poles.length === 0) {
      loadInitialData(); // Lade Daten falls noch nicht geladen
    }
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    if (stepId === 1 && poles.length === 0) {
      loadInitialData(); // Lade Daten falls noch nicht geladen
    }
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddPole = (pole, quantity = 1) => {
    setConfiguration(prev => ({
      ...prev,
      poles: [...prev.poles, { pole, quantity }]
    }));
  };

  const handleRemovePole = (index) => {
    setConfiguration(prev => ({
      ...prev,
      poles: prev.poles.filter((_, i) => i !== index)
    }));
  };

  const handleAccessoryToggle = (accessory, quantity = 1) => {
    setConfiguration(prev => {
      const currentAccessories = prev.accessories || [];
      const isSelected = currentAccessories.some(acc => acc.id === accessory.id);
      
      if (isSelected) {
        // Check if we're updating quantity or removing
        if (quantity > 0) {
          // Update quantity for existing accessory
          return {
            ...prev,
            accessory_quantities: {
              ...prev.accessory_quantities,
              [accessory.id]: quantity
            }
          };
        } else {
          // Remove accessory
          const newAccessories = currentAccessories.filter(acc => acc.id !== accessory.id);
          const newQuantities = { ...prev.accessory_quantities };
          delete newQuantities[accessory.id];
          
          return {
            ...prev,
            accessories: newAccessories,
            accessory_quantities: newQuantities
          };
        }
      } else {
        // Add accessory
        return {
          ...prev,
          accessories: [...currentAccessories, accessory],
          accessory_quantities: {
            ...prev.accessory_quantities,
            [accessory.id]: quantity
          }
        };
      }
    });
  };

  const handleAccessoryQuantityChange = (accessory, quantity) => {
    setConfiguration(prev => ({
      ...prev,
      accessory_quantities: {
        ...prev.accessory_quantities,
        [accessory.id]: quantity
      }
    }));
  };

  const handleTransportSelect = (transportOption) => {
    setConfiguration(prev => ({
      ...prev,
      transport_option: transportOption
    }));
  };

  const handleLoadConfiguration = (data) => {
    const cfg = data.configuration || {};
    setConfiguration({
      poles: cfg.poles || [],
      accessories: cfg.accessories || [],
      accessory_quantities: cfg.accessory_quantities || {},
      transport_option: cfg.transport_option || {},
      customer_info: cfg.customer_info || {},
      config_code: data.code
    });
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExcelExport = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/generate-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configuration)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `xortec_konfiguration_${new Date().getTime()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Fehler beim Generieren der Excel-Datei');
      }
    } catch (error) {
      console.error('Excel Export Fehler:', error);
      alert('Fehler beim Exportieren der Excel-Datei. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <LandingPageUpdated
            onStartConfiguration={handleStartConfiguration}
            onLoadConfiguration={handleLoadConfiguration}
          />
        );
      case 1:
        return (
          <PoleSelection
            poles={poles}
            configuredPoles={configuration.poles}
            onAddPole={handleAddPole}
            onRemovePole={handleRemovePole}
            onProceed={handleNext}
          />
        );
      case 2:
        return (
          <AccessorySelection
            accessories={accessories}
            selectedAccessories={configuration.accessories}
            accessoryQuantities={configuration.accessory_quantities}
            transportOption={configuration.transport_option}
            selectedPoles={configuration.poles}
            onToggle={handleAccessoryToggle}
            onQuantityChange={handleAccessoryQuantityChange}
            onTransportSelect={handleTransportSelect}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <Summary
            configuration={configuration}
            onExcelExport={handleExcelExport}
            onPrevious={handlePrevious}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  const appBgStyle = {
    backgroundColor: '#061225',
    backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${process.env.PUBLIC_URL || ''}/images/bg_waves.webp)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
  };

  if (loading && currentStep === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={appBgStyle}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen" style={appBgStyle}>
        {/* Header nur bei Konfigurationsschritten anzeigen */}
        {currentStep > 0 && <Header />}
        
        <main className={currentStep === 0 ? "" : "container mx-auto px-4 py-8"}>
          <div className={currentStep === 0 ? "" : "max-w-6xl mx-auto"}>
            {/* Schritt-Anzeige nur bei Konfigurationsschritten */}
            {currentStep > 0 && (
              <ConfiguratorStep 
                currentStep={currentStep}
                steps={steps}
                onStepClick={handleStepClick}
                configuration={configuration}
              />
            )}

            {/* Hauptinhalt */}
            <div className={currentStep === 0 ? "" : "mt-8"}>
              {renderStepContent()}
            </div>
          </div>
        </main>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;